import { XMarkIcon } from '@heroicons/react/16/solid';
import { Skeleton } from 'react-daisyui';
import { useModalsContext } from '../../../../../../providers/ModalsProvider/Modals.context';
import { eChannelType } from '../../../../../../providers/SocketsProvider/Sockets.enums';
import { useWorkspaceContext } from '../../../../../../providers/WorkspaceProvider/Workspace.context';
import useChatActions from '../../../../Chat.actions';
import { ChatEnum, ChatState } from '../../../../Chat.enum';
import { useChatContext } from '../../../../Chat.hook';
import { TemplateFbIg } from '../../../../Chat.types';
import { useMessageBarContext } from '../../MessageBar.hook';
import { useTemplatesActions } from './TemplateActions.hook';
import './TemplateActions.styles.scss';
import TemplateRow from './TemplateRow/TemplateRow.component';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';

export default function TemplateActions() {
  LLog.info(
    {
      component: LOG_COMPONENTS.CHAT,
      method: 'TemplateActions.component',
      operation: LOG_OPERATIONS.UI_RENDER,
    },
    'TemplateActions rendered',
  );
  const { templates, loading, error, refetch, changeChatState } =
    useTemplatesActions();
  const { setSelectedAction } = useMessageBarContext();
  const { sendMetaTemplateFbIg } = useChatActions();
  const { trackEvent } = useAnalytics();

  const { selectedChat, setSelectedChat, setBlockTextArea, selectedChatLeadId } =
    useChatContext();

  const { currentWorkspace } = useWorkspaceContext();

  const { selectedLeadForChatModal } = useModalsContext();

  const handleTemplateSend = async (template: TemplateFbIg) => {
    const capturedLeadId = selectedChatLeadId;
    setBlockTextArea(true);
    try {
      LLog.info(
        {
          component: LOG_COMPONENTS.CHAT,
          method: 'TemplateActions.component',
          operation: LOG_OPERATIONS.UI_INTERACTION,
        },
        'Selected template',
        { template },
      );
      await sendMetaTemplateFbIg(template.text);
      setSelectedAction(ChatEnum.CHAT);
      trackEvent('front.chat.template.instagram_messenger.select', {
        displayName: 'Elegir template para instagram o messenger',
        description:
          'Cuántas veces dieron click en una plantilla y la enviaron',
        integrationType:
          (selectedLeadForChatModal?.chat?.integration ===
            eChannelType.MESSENGER &&
            'Facebook') ||
          (selectedLeadForChatModal?.chat?.integration ===
            eChannelType.INSTAGRAM &&
            'Instagram'),
      });
      if (!selectedChat || !currentWorkspace || !selectedLeadForChatModal) {
        return;
      }
      setSelectedChat({
        ...selectedChat,
        state: ChatState.INACTIVE,
      });
      changeChatState(
        currentWorkspace?.id,
        selectedLeadForChatModal?.handle ?? '',
        {
          state: ChatState.INACTIVE,
          date: new Date().toISOString(),
          channelType:
            selectedLeadForChatModal?.chat?.integration ||
            eChannelType.WHATSAPP_WEB,
        },
      );
    } finally {
      if (selectedChatLeadId === capturedLeadId) {
        setBlockTextArea(false);
      }
    }
  };

  return (
    <div className="TemplatesActions relative">
      <div className="TemplatesActions__header">
        <h3>Plantillas</h3>
        <XMarkIcon
          onClick={() => setSelectedAction(ChatEnum.CHAT)}
          className="TemplatesActions__close-icon"
        />
      </div>
      {error ? (
        <div className="TemplatesActions__error">
          <p>Error loading templates: {error}</p>
          <button onClick={refetch}>Retry</button>
        </div>
      ) : (
        <ul className="TemplatesActions__list">
          {templates.map((template) => (
            <TemplateRow
              key={template.id}
              template={template}
              onSelect={handleTemplateSend}
            />
          ))}
        </ul>
      )}
      {loading && (
        <div className="TemplatesActions__loader absolute inset-0 flex items-center justify-center">
          <Skeleton className="w-full h-full" />
        </div>
      )}
    </div>
  );
}
